import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Place } from '@mui/icons-material'
import { Button, Grid, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import ScreenContainer from '../../components/screen'
import { useActivationContext } from '../../providers/activation-provider'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import { formatAddress } from '../../utils/helpers'

import useStyles from './index.styles'

const ConfirmLocation = () => {
  const { activationLocationState } = useActivationContext()
  const { folderId } = activationLocationState
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles

  const { ownUser } = useContext(AuthenticationContext)

  const [folder, setFolder] = useState<IFolderType | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  useEffect(() => {
    const fetchFolder = async () => {
      if (!folderId) return
      const res = await Api.get(`/api/folders/${folderId}`)
      if (!res.ok) {
        enqueueSnackbar('Could not get folder information', { variant: 'error' })
        return
      }
      setFolder(res.body)
    }
    fetchFolder()
  }, [enqueueSnackbar, folderId])

  const handleContinue = async () => {
    if (!ownUser) return

    setIsSubmitting(true)

    const resPatchAttributes = await Api.patch(`/api/v2/folders/${folderId}/attributes`, {
      stepConfirmUser: ownUser.id,
    })
    if (!resPatchAttributes.ok) {
      enqueueSnackbar(`Could not update the folder attributes.`, { variant: 'error' })
      setIsSubmitting(false)
      return
    }
    navigate('/participation-agreement', { state: { ...activationLocationState, fromDashboard: false } })
    setIsSubmitting(false)
  }

  return (
    <ScreenContainer loading={!!folderId && !folder} error={!folderId ? 'No location has been provided' : undefined} headerProps={{ showLogo: true }}>
      <Grid container={true} sx={classes.mainContainer}>
        {folder && (
          <>
            <Typography variant="h5">Confirm location</Typography>

            <Typography variant="body2" color="textSecondary" sx={classes.subtitleText}>
              You are about to set up this location
            </Typography>

            <Grid container={true} sx={classes.locationContainer}>
              <Place color="primary" sx={classes.placeIcon} />
              <Typography variant="subtitle1">{folder.name}</Typography>
              <Typography variant="body1" color="textSecondary" sx={classes.addressText} className="fs-exclude">
                {formatAddress(`${folder.information.address.address}`)}
                <br />
                {formatAddress(
                  `${folder.information.address.locality}, ${folder.information.address.region} ${folder.information.address.code} ${folder.information.address.country}`,
                  folder.information.address.country
                )}
              </Typography>
            </Grid>

            <Grid container={true} sx={classes.footer}>
              <Button onClick={() => navigate(-1)} variant="outlined" sx={classes.backButton}>
                Back
              </Button>
              <Button onClick={handleContinue} variant="contained" loading={isSubmitting} disabled={isSubmitting} sx={classes.continueButton}>
                Continue
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </ScreenContainer>
  )
}

export default ConfirmLocation
